import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { AddNoteModal, EssayTable, RecentBrainstormCard, SharedTaskRow } from './sharedDeskBits';
import { Eyebrow, SectionHead, dateLong, dateShort } from './essayShared';

// What a parent or advisor sees first: how long there is, what is being asked
// of them, and where every essay stands.
export default function SharedOverviewPage() {
  const { api } = useAuth();
  const { grant, grantId, desk, setDesk, reloadDesk } = useOutletContext();
  const [noteFor, setNoteFor] = useState(null);

  const { nextDeadline, stats, essays, myTasks, weekAhead, recentBrainstorms, hiddenEssayCount } = desk;
  const unshared = essays.filter((e) => !e.draftShared).length;

  const setTaskStatus = async (task, status, { notify } = {}) => {
    try {
      const res = await api.patch(`/college-access/shared/${grantId}/tasks/${task.id}`, {
        status,
        notify: notify === true,
      });
      setDesk((prev) => ({ ...prev, myTasks: prev.myTasks.map((t) => (t.id === task.id ? { ...t, status } : t)) }));
      if (res.data.delivery?.sent) toast.success(`Emailed ${grant.ownerName}`);
      else if (res.data.delivery?.error) toast.error("Saved, but the email didn't send");
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update that task');
    }
  };

  const postNote = async (essayId, body, { notify } = {}) => {
    const res = await api.post(`/college-access/shared/${grantId}/essays/${essayId}/notes`, {
      body,
      notify: notify === true,
    });
    await reloadDesk();
    setNoteFor(null);
    if (res.data.delivery?.sent) toast.success(`Note posted and emailed to ${grant.ownerName}`);
    else if (res.data.delivery?.error) toast.error("Note posted, but the email didn't send");
    else toast.success('Note posted');
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '36px 32px 64px' }}>
      <style>{`
        @media (max-width: 940px) {
          .desk-shared-hero, .desk-shared-body { grid-template-columns: minmax(0,1fr) !important; }
        }
      `}</style>

      <div className="desk-shared-hero" style={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: 28, alignItems: 'stretch' }}>
        <div style={{ background: 'var(--college-ink)', color: 'var(--college-bg)', padding: '28px 26px' }}>
          <p
            style={{
              font: "600 11px/1 'Instrument Sans', sans-serif",
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'rgba(245,240,230,0.55)',
              margin: '0 0 12px',
            }}
          >
            Next deadline
          </p>
          {nextDeadline ? (
            <>
              <div className="font-serif-editorial" style={{ fontSize: 88, lineHeight: 0.95, display: 'flex', alignItems: 'baseline' }}>
                {nextDeadline.days}
                <span style={{ fontSize: 30, fontStyle: 'italic', marginLeft: 10 }}>days</span>
              </div>
              <p style={{ margin: '16px 0 0', fontSize: 14.5, color: 'rgba(245,240,230,0.78)', lineHeight: 1.5 }}>
                until{' '}
                <span style={{ color: 'var(--college-bg)', borderBottom: '1px solid var(--college-gold)' }}>
                  {nextDeadline.collegeName}
                </span>{' '}
                {nextDeadline.planLabel || nextDeadline.plan} —{' '}
                <span className="font-mono-editorial">{dateLong(nextDeadline.deadline)}</span>
              </p>
            </>
          ) : (
            <p className="font-serif-editorial" style={{ fontSize: 34, fontStyle: 'italic', margin: 0, lineHeight: 1.2 }}>
              {grant.seeList ? 'No deadlines on the list yet' : 'Deadlines are not shared with you'}
            </p>
          )}

          <div style={{ display: 'flex', gap: 30, marginTop: 26, paddingTop: 18, borderTop: '1px solid rgba(241,233,216,0.18)', flexWrap: 'wrap' }}>
            {[
              { label: 'Essays', value: stats.essays },
              { label: 'Submitted', value: stats.submitted },
              { label: 'Not started', value: stats.notStarted },
              { label: 'Schools', value: stats.schools },
            ].map((stat) => (
              <div key={stat.label}>
                <p
                  style={{
                    margin: '0 0 3px',
                    font: "600 10px/1 'Instrument Sans', sans-serif",
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: 'rgba(245,240,230,0.5)',
                  }}
                >
                  {stat.label}
                </p>
                <p className="font-mono-editorial" style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
                  {stat.value}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            background: 'var(--college-panel)',
            border: '1px solid var(--college-border)',
            padding: '24px 22px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Eyebrow style={{ margin: '0 0 14px' }}>Your tasks</Eyebrow>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {myTasks.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--college-muted)' }}>
                Nothing on your plate — {grant.ownerName} hasn't asked you for anything yet.
              </p>
            ) : (
              myTasks.map((task) => <SharedTaskRow key={task.id} task={task} grantId={grantId} ownerName={grant.ownerName} onStatus={setTaskStatus} />)
            )}
          </div>
          {myTasks.length > 0 && (
            <p style={{ margin: '16px 0 0', fontSize: 12, color: 'var(--college-muted)' }}>
              You can mark these done, or decline — {grant.ownerName} sees either.
            </p>
          )}
        </div>
      </div>

      <div
        className="desk-shared-body"
        style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: 28, marginTop: 34, alignItems: 'start' }}
      >
        <div>
          <SectionHead
            title="Where the essays stand"
            size={24}
            right={
              <span style={{ fontSize: 11.5, color: 'var(--college-muted)' }}>
                {grant.readDrafts ? `${grant.ownerName} shares drafts with you` : `Drafts stay with ${grant.ownerName}`}
                {grant.comment ? ' · brainstorms are open to everyone' : ''}
              </span>
            }
          />
          <EssayTable essays={essays} grant={grant} grantId={grantId} onAddNote={setNoteFor} />

          {(hiddenEssayCount > 0 || unshared > 0) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginTop: 14,
                padding: '13px 16px',
                background: 'var(--college-bg-alt)',
                border: '1px dashed var(--college-border)',
              }}
            >
              <span style={{ fontSize: 12.5, color: 'var(--college-ink-soft)' }}>
                {hiddenEssayCount > 0
                  ? `${hiddenEssayCount} essays are tracked but not shared with you. `
                  : `${unshared} of these drafts aren't shared with you. `}
                {grant.ownerName} decides per person — you'll see stage and deadline, never the words, unless they turn
                it on.
              </span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div>
            <Eyebrow>This week</Eyebrow>
            <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)' }}>
              {weekAhead.length === 0 ? (
                <p style={{ margin: 0, padding: '12px 15px', fontSize: 12.5, color: 'var(--college-muted)' }}>
                  Nothing due in the next seven days.
                </p>
              ) : (
                weekAhead.map((item, i) => (
                  <div
                    key={`${item.label}-${i}`}
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: '12px 15px',
                      borderBottom: i === weekAhead.length - 1 ? 'none' : '1px solid var(--college-border-soft)',
                    }}
                  >
                    <span
                      className="font-mono-editorial"
                      style={{
                        fontSize: 11.5,
                        minWidth: 44,
                        flexShrink: 0,
                        color: i === 0 ? 'var(--college-red)' : 'var(--college-ink-soft)',
                      }}
                    >
                      {dateShort(item.date)}
                    </span>
                    <span style={{ fontSize: 12.5, color: 'var(--college-ink-soft)' }}>{item.label}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {grant.comment && (
            <div>
              <Eyebrow>Recent brainstorming</Eyebrow>
              <RecentBrainstormCard
                notes={recentBrainstorms}
                essays={essays}
                ownerName={grant.ownerName}
                onAddNote={setNoteFor}
              />
            </div>
          )}

          <div style={{ background: 'var(--college-bg-alt)', border: '1px solid var(--college-border-soft)', padding: 16 }}>
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--college-ink-soft)' }}>
              This view covers {grant.ownerName}'s college applications only. Their coursework, grades and school
              planner are not part of what they shared.
            </p>
          </div>
        </div>
      </div>

      {noteFor && (
        <AddNoteModal
          essays={essays}
          initialEssayId={noteFor.id}
          ownerName={grant.ownerName}
          onClose={() => setNoteFor(null)}
          onPost={postNote}
        />
      )}
    </div>
  );
}
